import { el } from '@ui/dom';
import { navigate } from '@ui/router';
import { isStorageConsentAccepted } from '@core/privacy';
import { arcadeCatalog, copy, type ArcadeId } from './catalog';
import { createEngine, type Engine } from './engines';
import { DIRECTIONS, type Cell } from './logic';
import { preview } from './drawing';

const memoryRecords = new Map<string, number>();
function readRecord(id: string): number {
  if (isStorageConsentAccepted()) {
    try { return Number(localStorage.getItem(`gamez.arcade.${id}`)) || 0; } catch { /* in-memory fallback */ }
  }
  return memoryRecords.get(id) ?? 0;
}

export function renderArcade(id: string): HTMLElement {
  const info = arcadeCatalog().find((g) => g.id === id);
  if (!info) return el('main', { class: 'arcade-page' }, [el('p', {}, [copy('Gioco non trovato.', 'Game not found.')]), el('button', { class: 'btn-primary', on: { click: () => navigate({ name: 'lobby' }) } }, ['← Gamez'])]);
  const gameId = info.id as ArcadeId;
  const abort = new AbortController();
  const root = el('main', { class: 'arcade-page', dataset: { arcade: gameId }, style: `--game-color:${info.color}` });
  let state: 'ready' | 'playing' | 'paused' | 'over' = 'ready';
  let score = 0, record = readRecord(gameId), remaining = 90, elapsed = 0, raf = 0, last = 0, engine: Engine;
  let soundEnabled = false, audioCtx: AudioContext | null = null, lastSound = 0, dragging = false, touchStart: { x: number; y: number } | null = null;
  const held = new Set<string>();
  const canvas = el('canvas', { class: 'arcade-canvas', width: 600, height: 480 });
  canvas.setAttribute('aria-label', `${info.name}. ${info.instructions}`); canvas.setAttribute('role', 'img');
  const ctx = canvas.getContext('2d')!;
  canvas.tabIndex = 0;
  const overlay = el('div', { class: 'arcade-overlay' });
  const stage = el('div', { class: 'arcade-stage' }, [canvas, overlay]);
  const scoreEl = el('strong', {}, ['0']), recordEl = el('strong', {}, [`${record}`]), timeEl = el('strong', {}, ['1:30']);
  const progressEl = el('span', { class: 'arcade-progress' });
  const message = el('p', { class: 'arcade-message' }, [copy('Il divertimento è una cosa seria. Quasi.', 'Having fun is serious business. Almost.')]);
  message.setAttribute('aria-live', 'polite');
  const pauseBtn = el('button', { class: 'btn-ghost', disabled: true, on: { click: () => togglePause() } }, [copy('Ⅱ Pausa', 'Ⅱ Pause')]);
  const soundBtn = el('button', { class: 'btn-ghost', on: { click: () => {
    soundEnabled = !soundEnabled; soundBtn.textContent = soundEnabled ? copy('♪ Audio attivo', '♪ Sound on') : copy('♪ Audio spento', '♪ Sound off');
    soundBtn.setAttribute('aria-pressed', String(soundEnabled));
    if (soundEnabled) { audioCtx ??= new AudioContext(); void audioCtx.resume(); playSound('power'); }
  } } }, [copy('♪ Audio spento', '♪ Sound off')]);
  soundBtn.setAttribute('aria-pressed', 'false');
  function playSound(kind: 'eat' | 'hit' | 'shoot' | 'power'): void {
    if (!soundEnabled || !audioCtx || audioCtx.state !== 'running' || audioCtx.currentTime - lastSound < .06) return;
    lastSound = audioCtx.currentTime;
    const oscillator = audioCtx.createOscillator(), gain = audioCtx.createGain();
    oscillator.type = kind === 'hit' ? 'sawtooth' : 'sine';
    oscillator.frequency.setValueAtTime({ eat: 620, hit: 110, shoot: 340, power: 840 }[kind], audioCtx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(kind === 'hit' ? 45 : 150, audioCtx.currentTime + .12);
    gain.gain.setValueAtTime(.045, audioCtx.currentTime); gain.gain.exponentialRampToValueAtTime(.001, audioCtx.currentTime + .15);
    oscillator.connect(gain); gain.connect(audioCtx.destination); oscillator.start(); oscillator.stop(audioCtx.currentTime + .16);
  }
  function resetInputs(): void { held.clear(); dragging = false; touchStart = null; engine?.fire(false); if (gameId === 'espresso') engine?.direction({ x: 0, y: 0 }); }
  function showOverlay(title: string, text: string, button: string, action: () => void): void {
    overlay.replaceChildren(el('div', { class: 'arcade-overlay-content' }, [
      el('span', { class: 'eyebrow' }, [state === 'over' ? 'GAME OVER · INSERT ANOTHER COFFEE' : info!.eyebrow]),
      el('h2', {}, [title]), el('p', {}, [text]), el('button', { class: 'btn-primary', on: { click: action } }, [button]),
      state === 'over' ? el('span', { class: 'arcade-result' }, [`${score} ${copy('punti', 'points')} · ${copy('record', 'best')} ${record}`]) : null,
    ]));
    overlay.hidden = false;
  }
  function finish(won: boolean, it: string, en: string): void {
    if (state !== 'playing') return;
    state = 'over'; resetInputs(); pauseBtn.disabled = true; cancelAnimationFrame(raf);
    if (score > record) {
      record = score; memoryRecords.set(gameId, record);
      if (isStorageConsentAccepted()) try { localStorage.setItem(`gamez.arcade.${gameId}`, `${record}`); } catch { /* memory remains available */ }
      recordEl.textContent = `${record}`;
    }
    draw(); showOverlay(won ? copy('Missione compiuta!', 'Mission complete!') : copy('Un’altra partita?', 'One more round?'), copy(it, en), copy('↻ Rivincita', '↻ Play again'), start);
  }
  function start(): void {
    score = 0; remaining = 90; elapsed = 0; state = 'playing'; last = 0; resetInputs(); overlay.hidden = true; pauseBtn.disabled = false;
    pauseBtn.textContent = copy('Ⅱ Pausa', 'Ⅱ Pause');
    engine = createEngine(gameId, {
      points(n) { score += n; scoreEl.textContent = `${score}`; },
      say(it, en) { message.textContent = copy(it, en); },
      sound: playSound,
      hit() { playSound('hit'); stage.classList.remove('damage'); void stage.offsetWidth; stage.classList.add('damage'); }, finish,
    });
    scoreEl.textContent = '0'; message.textContent = copy('Si parte. La dignità è facoltativa.', 'Here we go. Dignity is optional.');
    cancelAnimationFrame(raf); raf = requestAnimationFrame(frame);
    canvas.focus({ preventScroll: true });
  }
  function togglePause(): void {
    if (state === 'playing') {
      state = 'paused'; resetInputs(); cancelAnimationFrame(raf); pauseBtn.textContent = copy('▶ Riprendi', '▶ Resume');
      showOverlay(copy('Pausa meritata.', 'Well-earned break.'), copy('Il cronometro si ferma. Respira, stira le dita, salva il mondo dopo.', 'The clock stops. Breathe, stretch your fingers, save the world later.'), copy('▶ Riprendi', '▶ Resume'), togglePause);
    } else if (state === 'paused') {
      state = 'playing'; overlay.hidden = true; last = 0; pauseBtn.textContent = copy('Ⅱ Pausa', 'Ⅱ Pause'); raf = requestAnimationFrame(frame);
    }
  }
  function draw(): void {
    ctx.save(); ctx.clearRect(0, 0, 600, 480); ctx.fillStyle = gameId === 'espresso' ? '#242033' : '#1c2a23'; ctx.fillRect(0, 0, 600, 480);
    engine.draw(ctx, elapsed); ctx.restore(); progressEl.textContent = engine.progress();
    const seconds = Math.ceil(remaining); timeEl.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    timeEl.classList.toggle('time-low', seconds <= 15);
  }
  function frame(now: number): void {
    if (state !== 'playing') return;
    const actualDt = last ? Math.max(0, (now - last) / 1000) : 0;
    const dt = Math.min(actualDt, .05); last = now; elapsed += dt; remaining = Math.max(0, remaining - actualDt);
    engine.update(dt); draw();
    if (state !== 'playing') return;
    if (remaining <= 0) finish(false, 'Tempo scaduto. È già ora di una pausa dalla pausa.', 'Time’s up. Time for a break from your break.');
    else raf = requestAnimationFrame(frame);
  }
  const keyDirection: Record<string, Cell> = { ArrowUp: DIRECTIONS[0], w: DIRECTIONS[0], ArrowRight: DIRECTIONS[1], d: DIRECTIONS[1], ArrowDown: DIRECTIONS[2], s: DIRECTIONS[2], ArrowLeft: DIRECTIONS[3], a: DIRECTIONS[3] };
  const listen = (target: EventTarget, type: string, handler: EventListener): void => target.addEventListener(type, handler, { signal: abort.signal });
  listen(window, 'keydown', (event) => {
    const e = event as KeyboardEvent, key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if (e.ctrlKey || e.metaKey || e.altKey || (e.target instanceof HTMLElement && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName))) return;
    if (keyDirection[key] || key === ' ') e.preventDefault();
    if ((key === 'p' || key === 'Escape') && !e.repeat) togglePause();
    if (state !== 'playing') return;
    if (keyDirection[key]) { held.add(key); engine.direction(keyDirection[key]); }
    if (key === ' ') engine.fire(true);
  });
  listen(window, 'keyup', (event) => {
    const e = event as KeyboardEvent, key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    held.delete(key); if (key === ' ') engine?.fire(false);
    if (gameId === 'espresso' && keyDirection[key]) {
      const next = [...held].reverse().find((k) => keyDirection[k]); engine?.direction(next ? keyDirection[next] : { x: 0, y: 0 });
    }
  });
  listen(window, 'blur', () => { if (state === 'playing') togglePause(); });
  listen(document, 'visibilitychange', () => { if (document.hidden && state === 'playing') togglePause(); });
  listen(canvas, 'pointerdown', (event) => {
    if (state !== 'playing') return;
    const e = event as PointerEvent; dragging = true; canvas.setPointerCapture(e.pointerId); touchStart = { x: e.clientX, y: e.clientY };
    if (gameId === 'espresso') { engine.fire(true); movePointer(e); }
  });
  function movePointer(e: PointerEvent): void { const rect = canvas.getBoundingClientRect(); engine.pointer?.((e.clientX - rect.left) / rect.width * 600); }
  listen(canvas, 'pointermove', (event) => { if (dragging && state === 'playing' && gameId === 'espresso') movePointer(event as PointerEvent); });
  listen(canvas, 'pointerup', (event) => {
    const e = event as PointerEvent;
    if (touchStart && state === 'playing' && gameId !== 'espresso') {
      const dx = e.clientX - touchStart.x, dy = e.clientY - touchStart.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > 12) engine.direction(Math.abs(dx) > Math.abs(dy) ? { x: Math.sign(dx), y: 0 } : { x: 0, y: Math.sign(dy) });
    }
    dragging = false; touchStart = null; engine?.fire(false);
  });
  listen(canvas, 'pointercancel', () => resetInputs());
  function control(label: string, dir?: Cell): HTMLButtonElement {
    const btn = el('button', { class: `arcade-control ${dir ? '' : 'fire-control'}` }, [label]);
    btn.setAttribute('aria-label', dir ? copy('Muovi ', 'Move ') + label : copy('Spara caffè', 'Shoot coffee'));
    listen(btn, 'pointerdown', (event) => {
      if (state !== 'playing') return;
      const e = event as PointerEvent; e.preventDefault(); btn.setPointerCapture(e.pointerId);
      if (dir) engine.direction(dir); else engine.fire(true);
    });
    const release = () => { if (!dir) engine?.fire(false); else if (gameId === 'espresso') engine?.direction({ x: 0, y: 0 }); };
    listen(btn, 'pointerup', release); listen(btn, 'pointercancel', release); listen(btn, 'lostpointercapture', release);
    listen(btn, 'click', (event) => { if ((event as MouseEvent).detail === 0 && state === 'playing') { if (dir) engine.direction(dir); else { engine.fire(true); setTimeout(() => engine.fire(false), 250); } } });
    return btn;
  }
  const pad = gameId === 'espresso'
    ? el('div', { class: 'arcade-controls shooter-controls' }, [control('←', DIRECTIONS[3]), control(copy('☕ SPARA', '☕ FIRE')), control('→', DIRECTIONS[1])])
    : el('div', { class: 'arcade-controls direction-pad' }, [control('↑', DIRECTIONS[0]), control('←', DIRECTIONS[3]), control('↓', DIRECTIONS[2]), control('→', DIRECTIONS[1])]);
  const stat = (label: string, value: HTMLElement) => el('div', { class: 'arcade-stat' }, [el('span', {}, [label]), value]);
  root.append(
    el('header', { class: 'arcade-header' }, [el('button', { class: 'back-link', on: { click: () => navigate({ name: 'lobby' }) } }, ['← Gamez']), el('span', { class: 'eyebrow' }, ['THE QUICK PLAY CLUB']), soundBtn]),
    el('div', { class: 'arcade-heading' }, [el('span', { class: 'eyebrow' }, [copy('ARCADE / 90 SECONDI DI FELICITÀ', 'ARCADE / 90 SECONDS OF JOY')]), el('h1', {}, [info.name]), el('p', {}, [info.description])]),
    el('div', { class: 'arcade-layout' }, [
      el('section', { class: 'arcade-game' }, [el('div', { class: 'arcade-hud' }, [stat(copy('PUNTI', 'SCORE'), scoreEl), stat(copy('TEMPO', 'TIME'), timeEl), stat('RECORD', recordEl)]), stage, el('div', { class: 'arcade-bottom' }, [progressEl, pauseBtn]), message, pad]),
      el('aside', { class: 'arcade-guide' }, [el('span', { class: 'eyebrow' }, [copy('MANUALE DI SOPRAVVIVENZA', 'SURVIVAL MANUAL')]), el('h2', {}, [copy('Facile iniziare.\nDifficile smettere.', 'Easy to start.\nHard to stop.')]), el('h3', {}, [copy('01 / I comandi', '01 / The controls')]), el('p', {}, [info.instructions]), el('h3', {}, [copy('02 / Il colpo di genio', '02 / The twist')]), el('p', {}, [info.twist]), el('div', { class: 'guide-note' }, [copy('P o Esc per la pausa. Il gioco si ferma anche quando cambi scheda. Il record resta nel tuo browser se accetti i salvataggi.', 'P or Esc to pause. The game also pauses when you switch tabs. Your best score stays in your browser if you accept storage.')]), el('button', { class: 'back-link', on: { click: () => navigate({ name: 'lobby' }) } }, [copy('Scopri gli altri giochi ↗', 'Explore more games ↗')])]),
    ]),
  );
  engine = createEngine(gameId, { points() {}, say() {}, hit() {}, finish() {}, sound() {} }); draw();
  showOverlay(info.name, copy('90 secondi. Nessuna registrazione. Una pessima scusa per un’ultima partita.', '90 seconds. No signup. A terrible excuse for one last round.'), copy('▶ Si gioca', '▶ Let’s play'), start);
  // Routed views own their resources; cleanup also works on route refreshes.
  (root as HTMLElement & { dispose?: () => void }).dispose = () => { abort.abort(); cancelAnimationFrame(raf); resetInputs(); void audioCtx?.close(); };
  return root;
}

export function arcadePreview(id: string): HTMLCanvasElement {
  const canvas = el('canvas', { class: 'game-art' }); canvas.setAttribute('aria-hidden', 'true'); preview(canvas, id); return canvas;
}
